/**
 * Tenant Management API Service
 * Provides methods for managing O365 tenant registrations.
 * All operations require authentication.
 */

import { ApiService } from './api';
import type {
  TenantRegistrationRequest,
  TenantDetails,
  PaginatedTenants,
  TenantUpdateRequest,
  TenantAccessVerification,
  TenantAccessResponse,
  TenantUserWithAccess,
  TenantStatus,
  AnalyzeUpnResponse,
  RevokeAccessResponse,
  RedemptionCompletionResponse,
} from '../types/tenant';
import type { Team } from '../types/user';
import type {
  SharePointSite,
  SharePointDocument,
  FolderItem,
  UploadedFileItem,
  UploadSession,
  UploadChunkResponse,
  CreateFoldersResponse,
  SearchResultItem,
  SharedDocument,
  FileActivity,
  TeamMember,
  CreateChannelRequest,
  CreatedChannel,
  TeamChannel,
} from '../types/sharepoint';

/**
 * Tenant Management API Client
 */
class TenantApiServiceClass {
  /**
   * Get base URL for tenant endpoints.
   */
  private getBaseUrl(): string {
    return '/api/tenants';
  }

  /**
   * Register an existing O365 tenant for management.
   *
   * @param data - Tenant registration details
   * @returns Registered tenant details
   * @throws Error if tenant already exists or validation fails
   */
  async registerTenant(data: TenantRegistrationRequest): Promise<TenantDetails> {
    const response = await ApiService.request<TenantDetails>(
      `${this.getBaseUrl()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    return response;
  }

  /**
   * List all registered tenants with optional filtering and pagination.
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of tenants
   */
  async listTenants(params?: {
    status?: TenantStatus;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedTenants> {
    const queryParams = new URLSearchParams();

    if (params?.status) {
      queryParams.append('status_filter', params.status);
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.page_size) {
      queryParams.append('page_size', params.page_size.toString());
    }

    const url = queryParams.toString()
      ? `${this.getBaseUrl()}?${queryParams.toString()}`
      : this.getBaseUrl();

    const response = await ApiService.request<PaginatedTenants>(url, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Get detailed information for a specific tenant.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @returns Detailed tenant information
   * @throws Error if tenant not found
   */
  async getTenant(tenantId: string): Promise<TenantDetails> {
    const response = await ApiService.request<TenantDetails>(
      `${this.getBaseUrl()}/${tenantId}`,
      {
        method: 'GET',
      }
    );
    return response;
  }

  /**
   * Update tenant metadata (name, notes, status).
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @param data - Fields to update
   * @returns Updated tenant details
   * @throws Error if tenant not found
   */
  async updateTenant(
    tenantId: string,
    data: TenantUpdateRequest
  ): Promise<TenantDetails> {
    const response = await ApiService.request<TenantDetails>(
      `${this.getBaseUrl()}/${tenantId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    return response;
  }

  /**
   * Deactivate tenant (hard delete).
   * Both enterprise apps are always removed from Azure AD automatically.
   * Requires superadmin privileges.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @throws Error if user is not superadmin or tenant not found
   */
  async deactivateTenant(tenantId: string): Promise<void> {
    await ApiService.request<{ success: boolean; message: string }>(
      `${this.getBaseUrl()}/${tenantId}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Verify that the application has Graph API access to the tenant.
   * Tests connectivity and updates last_admin_consent timestamp on success.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @returns Access verification result with success/failure details
   */
  async verifyAccess(tenantId: string): Promise<TenantAccessVerification> {
    const response = await ApiService.request<TenantAccessVerification>(
      `${this.getBaseUrl()}/${tenantId}/verify`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  /**
   * Get the list of apps that require admin consent.
   * This is a fast endpoint that doesn't call Graph API.
   *
   * @returns List of apps with client_id and name
   */
  async getConsentApps(): Promise<{ apps: Array<{ client_id: string; name: string }> }> {
    const response = await ApiService.request<{ apps: Array<{ client_id: string; name: string }> }>(
      `${this.getBaseUrl()}/consent-apps`,
      {
        method: 'GET',
      }
    );
    return response;
  }

  /**
   * Get the Microsoft Teams the authenticated user is a member of in this tenant.
   *
   * The backend extracts the user's home UPN from their JWT token and finds their
   * guest account in the managed tenant to retrieve their teams.
   *
   * @param tenantId - The managed tenant ID to query for teams
   * @param refresh - If true, bypass backend TTL cache and fetch fresh data from Graph API
   * @returns List of teams the user is a member of
   */
  async getMyTeams(tenantId: string, refresh?: boolean): Promise<Team[]> {
    const url = refresh
      ? `${this.getBaseUrl()}/${tenantId}/my-teams?refresh=true`
      : `${this.getBaseUrl()}/${tenantId}/my-teams`;
    return ApiService.request<Team[]>(url, { method: 'GET' });
  }

  /**
   * Get SharePoint sites the authenticated user has access to in a managed tenant.
   *
   * Uses delegated permissions via OBO flow - only returns sites the user can access.
   *
   * @param tenantId - The managed tenant ID to query for sites
   * @param refresh - If true, bypass backend TTL cache and fetch fresh data from Graph API
   * @returns List of SharePoint sites the user has access to
   */
  async getMySites(tenantId: string, refresh?: boolean): Promise<SharePointSite[]> {
    const url = refresh
      ? `${this.getBaseUrl()}/${tenantId}/my-sites?refresh=true`
      : `${this.getBaseUrl()}/${tenantId}/my-sites`;
    return ApiService.request<SharePointSite[]>(url, { method: 'GET' });
  }

  /**
   * Get documents from a SharePoint site's document library.
   *
   * Uses delegated permissions via OBO flow - only returns documents the user can access.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param refresh - If true, bypass backend TTL cache and fetch fresh data from Graph API
   * @returns List of documents and folders in the site's default document library
   */
  async getSiteDocuments(tenantId: string, siteId: string, refresh?: boolean): Promise<SharePointDocument[]> {
    const url = refresh
      ? `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/documents?refresh=true`
      : `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/documents`;
    return ApiService.request<SharePointDocument[]>(url, { method: 'GET' });
  }

  /**
   * Get recently accessed/modified documents for the authenticated user.
   *
   * Uses delegated permissions via OBO flow - returns documents the user has
   * recently accessed or modified across all sites in the tenant.
   *
   * @param tenantId - The managed tenant ID
   * @param limit - Maximum number of documents to return (default 10, max 50)
   * @param refresh - If true, bypass backend TTL cache and fetch fresh data from Graph API
   * @returns List of recently accessed/modified documents
   */
  async getRecentDocuments(tenantId: string, limit?: number, refresh?: boolean): Promise<SharePointDocument[]> {
    const queryParams = new URLSearchParams();
    if (limit !== undefined) {
      queryParams.append('limit', limit.toString());
    }
    if (refresh) {
      queryParams.append('refresh', 'true');
    }

    const url = queryParams.toString()
      ? `${this.getBaseUrl()}/${tenantId}/my-recent-documents?${queryParams.toString()}`
      : `${this.getBaseUrl()}/${tenantId}/my-recent-documents`;

    return ApiService.request<SharePointDocument[]>(url, { method: 'GET' });
  }

  /**
   * Get contents of a folder in a SharePoint site's document library.
   *
   * Returns files and subfolders within the specified folder.
   * Use folderId="root" to get the root folder contents.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The folder item ID (use "root" for root folder)
   * @param refresh - If true, bypass backend TTL cache and fetch fresh data from Graph API
   * @returns List of items (files and folders) in the folder
   */
  async getFolderContents(
    tenantId: string,
    siteId: string,
    folderId: string,
    refresh?: boolean
  ): Promise<FolderItem[]> {
    const url = refresh
      ? `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/children?refresh=true`
      : `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/children`;
    return ApiService.request<FolderItem[]>(url, { method: 'GET' });
  }

  /**
   * Upload a file to a SharePoint folder.
   *
   * Uses simple upload for files <=4MB.
   * For larger files, use createUploadSession() and uploadChunk() instead.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID (use "root" for root folder)
   * @param file - The file to upload
   * @returns Uploaded file metadata
   */
  async uploadFile(
    tenantId: string,
    siteId: string,
    folderId: string,
    file: File
  ): Promise<UploadedFileItem> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/upload`;
    return ApiService.request<UploadedFileItem>(url, {
      method: 'POST',
      body: formData,
    });
  }

  /**
   * Create an upload session for resumable upload of large files.
   *
   * Use this for files >4MB. After creating the session, use uploadChunk()
   * to upload the file in chunks directly to SharePoint.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID (use "root" for root folder)
   * @param filename - The name of the file to be uploaded
   * @returns Upload session with uploadUrl and expiration time
   */
  async createUploadSession(
    tenantId: string,
    siteId: string,
    folderId: string,
    filename: string
  ): Promise<UploadSession> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/upload-session`;
    return ApiService.request<UploadSession>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filename }),
    });
  }

  /**
   * Upload a chunk of data directly to SharePoint using the upload session URL.
   *
   * This method uploads directly to SharePoint's upload endpoint (not through our backend).
   * Call this repeatedly with different chunks until the file is fully uploaded.
   *
   * @param uploadUrl - The uploadUrl from createUploadSession()
   * @param chunk - The chunk data to upload
   * @param contentRange - The Content-Range header value (e.g., "bytes 0-999999/5000000")
   * @returns Upload status (partial with nextExpectedRanges, or complete with file metadata)
   */
  async uploadChunk(
    uploadUrl: string,
    chunk: Uint8Array,
    contentRange: string
  ): Promise<UploadChunkResponse> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Range': contentRange,
        'Content-Type': 'application/octet-stream',
      },
      body: (chunk.buffer as ArrayBuffer).slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength),
    });

    if (!response.ok) {
      throw new Error(`Upload chunk failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create multiple folders in a SharePoint site.
   *
   * Creates folders in the specified paths, creating parent folders as needed.
   * Useful for preserving folder structure during folder uploads.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID to create folders under (use "root" for root)
   * @param folderPaths - Array of relative folder paths to create (e.g., ["subfolder", "subfolder/nested"])
   * @returns List of created folders with their IDs and paths
   */
  async createFolders(
    tenantId: string,
    siteId: string,
    folderId: string,
    folderPaths: string[]
  ): Promise<CreateFoldersResponse> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/create-folders`;
    return ApiService.request<CreateFoldersResponse>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ folder_paths: folderPaths }),
    });
  }

  /**
   * Search for files and folders within a SharePoint site.
   *
   * Searches across all folders within the site's default drive for matching
   * file/folder names. Uses Microsoft Graph API search endpoint.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param query - Search query string (searches file/folder names)
   * @returns List of matching items with id, name, webUrl, parentReference, etc.
   */
  async searchSite(
    tenantId: string,
    siteId: string,
    query: string
  ): Promise<SearchResultItem[]> {
    const encodedQuery = encodeURIComponent(query);
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/search?q=${encodedQuery}`;
    return ApiService.request<SearchResultItem[]>(url, { method: 'GET' });
  }

  /**
   * Create a new empty document in a SharePoint folder.
   *
   * Creates an empty document with the specified filename and returns
   * the document metadata including webUrl for opening in browser.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID (use "root" for root folder)
   * @param filename - The name of the document to create (e.g., "Document.docx")
   * @returns Created document metadata with id, name, webUrl
   */
  async createDocument(
    tenantId: string,
    siteId: string,
    folderId: string,
    filename: string
  ): Promise<FolderItem> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/create-document`;
    return ApiService.request<FolderItem>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filename }),
    });
  }

  /**
   * Get files shared with the authenticated user in a managed tenant.
   *
   * Uses delegated permissions via OBO flow - returns documents that other
   * users have shared directly with the current user.
   *
   * @param tenantId - The managed tenant ID
   * @param limit - Maximum number of documents to return (default 10, max 50)
   * @returns List of shared documents with sharer information
   */
  async getSharedWithMe(tenantId: string, limit?: number): Promise<SharedDocument[]> {
    const queryParams = new URLSearchParams();
    if (limit !== undefined) {
      queryParams.append('limit', limit.toString());
    }

    const url = queryParams.toString()
      ? `${this.getBaseUrl()}/${tenantId}/my-shared-documents?${queryParams.toString()}`
      : `${this.getBaseUrl()}/${tenantId}/my-shared-documents`;

    return ApiService.request<SharedDocument[]>(url, { method: 'GET' });
  }

  /**
   * Get recent file activity for the authenticated user in a managed tenant.
   *
   * Uses delegated permissions via OBO flow - returns recent actions
   * (edits, creates, deletes, etc.) on files the user has access to.
   *
   * @param tenantId - The managed tenant ID
   * @param limit - Maximum number of activities to return (default 10, max 50)
   * @returns List of file activity records
   */
  async getFileActivity(tenantId: string, limit?: number): Promise<FileActivity[]> {
    const queryParams = new URLSearchParams();
    if (limit !== undefined) {
      queryParams.append('limit', limit.toString());
    }

    const url = queryParams.toString()
      ? `${this.getBaseUrl()}/${tenantId}/my-file-activity?${queryParams.toString()}`
      : `${this.getBaseUrl()}/${tenantId}/my-file-activity`;

    return ApiService.request<FileActivity[]>(url, { method: 'GET' });
  }

  /**
   * Get members of a Microsoft Team.
   *
   * @param tenantId - The managed tenant ID
   * @param teamId - The Microsoft Team ID
   * @returns List of team members
   */
  async getTeamMembers(tenantId: string, teamId: string): Promise<TeamMember[]> {
    const url = `${this.getBaseUrl()}/${tenantId}/teams/${teamId}/members`;
    return ApiService.request<TeamMember[]>(url, { method: 'GET' });
  }

  /**
   * Create a new channel in a Microsoft Team.
   *
   * @param tenantId - The managed tenant ID
   * @param teamId - The Microsoft Team ID
   * @param data - Channel creation data (displayName, description, membershipType)
   * @returns Created channel details
   */
  async createChannel(
    tenantId: string,
    teamId: string,
    data: CreateChannelRequest
  ): Promise<CreatedChannel> {
    const url = `${this.getBaseUrl()}/${tenantId}/teams/${teamId}/channels`;
    return ApiService.request<CreatedChannel>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        display_name: data.displayName,
        description: data.description,
        membership_type: data.membershipType ?? 'standard',
      }),
    });
  }

  /**
   * Get channels for a Microsoft Team.
   *
   * @param tenantId - The managed tenant ID
   * @param teamId - The Microsoft Team ID
   * @returns List of team channels
   */
  async getTeamChannels(tenantId: string, teamId: string): Promise<TeamChannel[]> {
    const url = `${this.getBaseUrl()}/${tenantId}/teams/${teamId}/channels`;
    return ApiService.request<TeamChannel[]>(url, { method: 'GET' });
  }

  /**
   * Delete a file or folder from SharePoint.
   *
   * The item is moved to the site's recycle bin and can be restored
   * within the retention period.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to delete
   */
  async deleteItem(
    tenantId: string,
    siteId: string,
    itemId: string
  ): Promise<void> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}`;
    await ApiService.request<void>(url, {
      method: 'DELETE',
    });
  }

  /**
   * Move a file or folder to a different location in SharePoint.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to move
   * @param destinationFolderId - The ID of the destination folder
   * @returns Updated item metadata from Graph API
   */
  async moveItem(
    tenantId: string,
    siteId: string,
    itemId: string,
    destinationFolderId: string
  ): Promise<Record<string, unknown>> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/move`;
    return ApiService.request<Record<string, unknown>>(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ destination_folder_id: destinationFolderId }),
    });
  }

  /**
   * Rename a file or folder in SharePoint.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to rename
   * @param newName - The new name for the item
   * @returns Updated item metadata from Graph API
   */
  async renameItem(
    tenantId: string,
    siteId: string,
    itemId: string,
    newName: string
  ): Promise<Record<string, unknown>> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/rename`;
    return ApiService.request<Record<string, unknown>>(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ new_name: newName }),
    });
  }

  /**
   * Get download URL for a file in SharePoint.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file to download
   * @returns Object with download_url and name
   */
  async getDownloadUrl(
    tenantId: string,
    siteId: string,
    itemId: string
  ): Promise<{ download_url: string; name: string }> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/download`;
    return ApiService.request<{ download_url: string; name: string }>(url, {
      method: 'GET',
    });
  }

  /**
   * Copy a file or folder to a different location in SharePoint.
   *
   * Copy is asynchronous - returns a monitor URL to check status.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to copy
   * @param destinationFolderId - The ID of the destination folder
   * @returns Copy operation status with monitor URL
   */
  async copyItem(
    tenantId: string,
    siteId: string,
    itemId: string,
    destinationFolderId: string
  ): Promise<{ status: string; monitor_url: string }> {
    const url = `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/copy`;
    return ApiService.request<{ status: string; monitor_url: string }>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ destination_folder_id: destinationFolderId }),
    });
  }

  // ============================================================================
  // DELEGATED SHAREPOINT METHODS (OBO Flow - User-Attributed Operations)
  // ============================================================================

  /**
   * Get folder contents using delegated permissions (user's identity).
   * Modified by field will show actual user instead of "SharePoint App".
   *
   * IMPORTANT: Uses X-Delegated-Token header, NOT Authorization header.
   * AuthMiddleware validates Authorization tokens and would reject the delegated token.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The folder item ID (use "root" for root folder)
   * @param userToken - The user's delegated access token for OBO flow
   * @returns List of items (files and folders) in the folder
   */
  async getFolderContentsDelegated(
    tenantId: string,
    siteId: string,
    folderId: string,
    userToken: string
  ): Promise<FolderItem[]> {
    return ApiService.request<FolderItem[]>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/children/delegated`,
      {
        method: 'GET',
        headers: {
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
      }
    );
  }

  /**
   * Rename item using delegated permissions (user's identity).
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to rename
   * @param newName - The new name for the item
   * @param userToken - The user's delegated access token for OBO flow
   * @returns Updated item metadata
   */
  async renameItemDelegated(
    tenantId: string,
    siteId: string,
    itemId: string,
    newName: string,
    userToken: string
  ): Promise<{ id: string; name: string }> {
    return ApiService.request<{ id: string; name: string }>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/rename/delegated`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: JSON.stringify({ new_name: newName }),
      }
    );
  }

  /**
   * Move item using delegated permissions (user's identity).
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to move
   * @param destinationFolderId - The ID of the destination folder
   * @param userToken - The user's delegated access token for OBO flow
   * @returns Updated item metadata
   */
  async moveItemDelegated(
    tenantId: string,
    siteId: string,
    itemId: string,
    destinationFolderId: string,
    userToken: string
  ): Promise<{ id: string }> {
    return ApiService.request<{ id: string }>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/move/delegated`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: JSON.stringify({ destination_folder_id: destinationFolderId }),
      }
    );
  }

  /**
   * Copy item using delegated permissions (user's identity).
   *
   * Copy is asynchronous - returns a monitor URL to check status.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to copy
   * @param destinationFolderId - The ID of the destination folder
   * @param userToken - The user's delegated access token for OBO flow
   * @returns Copy operation status with monitor URL
   */
  async copyItemDelegated(
    tenantId: string,
    siteId: string,
    itemId: string,
    destinationFolderId: string,
    userToken: string
  ): Promise<{ status: string; monitor_url?: string }> {
    return ApiService.request<{ status: string; monitor_url?: string }>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/copy/delegated`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: JSON.stringify({ destination_folder_id: destinationFolderId }),
      }
    );
  }

  /**
   * Delete item using delegated permissions (user's identity).
   *
   * The item is moved to the site's recycle bin and can be restored
   * within the retention period.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param itemId - The ID of the file or folder to delete
   * @param userToken - The user's delegated access token for OBO flow
   */
  async deleteItemDelegated(
    tenantId: string,
    siteId: string,
    itemId: string,
    userToken: string
  ): Promise<void> {
    return ApiService.request<void>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/items/${itemId}/delegated`,
      {
        method: 'DELETE',
        headers: {
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
      }
    );
  }

  /**
   * Create folder using delegated permissions (user's identity).
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param parentFolderId - The ID of the parent folder
   * @param folderName - The name of the folder to create
   * @param userToken - The user's delegated access token for OBO flow
   * @returns Created folder metadata
   */
  async createFolderDelegated(
    tenantId: string,
    siteId: string,
    parentFolderId: string,
    folderName: string,
    userToken: string
  ): Promise<FolderItem> {
    return ApiService.request<FolderItem>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${parentFolderId}/children/delegated`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: JSON.stringify({ name: folderName }),
      }
    );
  }

  /**
   * Upload a file using delegated permissions (user's identity).
   * Modified by field will show actual user instead of "SharePoint App".
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID (use "root" for root folder)
   * @param file - The file to upload
   * @param userToken - The user's delegated access token for OBO flow
   * @returns Uploaded file metadata
   */
  async uploadFileDelegated(
    tenantId: string,
    siteId: string,
    folderId: string,
    file: File,
    userToken: string
  ): Promise<UploadedFileItem> {
    const formData = new FormData();
    formData.append('file', file);

    return ApiService.request<UploadedFileItem>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/upload/delegated`,
      {
        method: 'POST',
        headers: {
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: formData,
      }
    );
  }

  /**
   * Create an upload session using delegated permissions (user's identity).
   * Use this for files >4MB. Returns uploadUrl for chunked upload.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID (use "root" for root folder)
   * @param filename - The name of the file to be uploaded
   * @param userToken - The user's delegated access token for OBO flow
   * @returns Upload session with uploadUrl and expiration time
   */
  async createUploadSessionDelegated(
    tenantId: string,
    siteId: string,
    folderId: string,
    filename: string,
    userToken: string
  ): Promise<UploadSession> {
    return ApiService.request<UploadSession>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/upload-session/delegated`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: JSON.stringify({ filename }),
      }
    );
  }

  /**
   * Create a new empty document using delegated permissions (user's identity).
   * Modified by field will show actual user instead of "SharePoint App".
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID (use "root" for root folder)
   * @param filename - The name of the document to create (e.g., "Document.docx")
   * @param userToken - The user's delegated access token for OBO flow
   * @returns Created document metadata
   */
  async createDocumentDelegated(
    tenantId: string,
    siteId: string,
    folderId: string,
    filename: string,
    userToken: string
  ): Promise<FolderItem> {
    return ApiService.request<FolderItem>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/create-document/delegated`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: JSON.stringify({ filename }),
      }
    );
  }

  /**
   * Create multiple folders using delegated permissions (user's identity).
   * Creates folders in the specified paths, creating parent folders as needed.
   *
   * @param tenantId - The managed tenant ID
   * @param siteId - The SharePoint site ID
   * @param folderId - The parent folder ID to create folders under (use "root" for root)
   * @param folderPaths - Array of relative folder paths to create (e.g., ["subfolder", "subfolder/nested"])
   * @param userToken - The user's delegated access token for OBO flow
   * @returns List of created folders with their IDs and paths
   */
  async createFoldersDelegated(
    tenantId: string,
    siteId: string,
    folderId: string,
    folderPaths: string[],
    userToken: string
  ): Promise<CreateFoldersResponse> {
    return ApiService.request<CreateFoldersResponse>(
      `${this.getBaseUrl()}/${tenantId}/my-sites/${siteId}/folders/${folderId}/create-folders/delegated`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Delegated-Token': userToken, // NOT Authorization header!
        },
        body: JSON.stringify({ folder_paths: folderPaths }),
      }
    );
  }

  /**
   * Grant a user access to a tenant (cross-organization capable).
   *
   * This endpoint allows admins/owners of the organization that OWNS a tenant
   * to grant access to that tenant to ANY user, regardless of which organization
   * the user belongs to.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @param userId - Database ID of the user to grant access to
   * @param matterRole - Optional matter role for the access grant
   * @returns TenantAccessResponse with access record details
   * @throws Error if user is not authorized or validation fails
   */
  async grantUserTenantAccess(
    tenantId: string,
    userId: number,
    matterRole?: string
  ): Promise<TenantAccessResponse> {
    const url = `${this.getBaseUrl()}/${tenantId}/users/${userId}/grant-access`;
    return ApiService.request<TenantAccessResponse>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ matter_role: matterRole }),
    });
  }

  /**
   * Get list of users with access to a tenant.
   *
   * Returns all users who have been granted access to the specified tenant,
   * including their organization, role, and guest provisioning status.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @returns List of users with access to the tenant
   */
  async getTenantUserAccess(tenantId: string): Promise<TenantUserWithAccess[]> {
    const url = `${this.getBaseUrl()}/${tenantId}/user-access`;
    return ApiService.request<TenantUserWithAccess[]>(url, {
      method: 'GET',
    });
  }

  /**
   * Analyze a UPN/email to determine what grant action can be taken.
   *
   * This endpoint analyzes the domain of a UPN/email and returns what action
   * can be taken based on whether the user exists and which organization
   * (if any) owns the domain.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @param upn - User Principal Name or email to analyze
   * @returns Analysis result with status and relevant info
   */
  async analyzeUpn(tenantId: string, upn: string): Promise<AnalyzeUpnResponse> {
    const url = `${this.getBaseUrl()}/${tenantId}/analyze-upn`;
    return ApiService.request<AnalyzeUpnResponse>(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ upn }),
    });
  }

  /**
   * Revoke a user's access to a tenant (cross-organization capable).
   *
   * This endpoint allows admins/owners of the organization that OWNS a tenant
   * to revoke access to that tenant from ANY user, regardless of which
   * organization the user belongs to.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @param userId - Database ID of the user to revoke access from
   * @returns RevokeAccessResponse with operation result
   * @throws Error if user is not authorized or validation fails
   */
  async revokeUserTenantAccess(
    tenantId: string,
    userId: number
  ): Promise<RevokeAccessResponse> {
    const url = `${this.getBaseUrl()}/${tenantId}/users/${userId}/revoke-access`;
    return ApiService.request<RevokeAccessResponse>(url, {
      method: 'DELETE',
    });
  }

  /**
   * Complete B2B invitation redemption for a tenant.
   *
   * Called after a federated organization user has completed the consent flow
   * via the inviteRedeemUrl. Updates the redemption_status to "completed"
   * and makes the tenant accessible in the tenant list.
   *
   * @param tenantId - Azure AD tenant ID (GUID)
   * @returns RedemptionCompletionResponse with status confirmation
   * @throws Error if user doesn't have access to the tenant
   */
  async completeRedemption(tenantId: string): Promise<RedemptionCompletionResponse> {
    const url = `${this.getBaseUrl()}/${tenantId}/complete-redemption`;
    return ApiService.request<RedemptionCompletionResponse>(url, {
      method: 'POST',
    });
  }
}

// Export singleton instance
export const TenantApiService = new TenantApiServiceClass();
